import { PropsWithChildren, useCallback, useRef, useState } from 'react'

import { ModalTypes } from '../registry'
import { Context } from '../context'
import { ModalRenderer } from './ModalRenderer'

interface QueuedModal {
    type: ModalTypes
    props?: Record<string, unknown>
}

export const ModalProvider = ({ children }: PropsWithChildren) => {
    const [type, setType] = useState<ModalTypes>()
    const [props, setProps] = useState<Record<string, unknown>>()
    const queueRef = useRef<QueuedModal[]>([])
    const activeRef = useRef(false)

    const openModal = useCallback(<Props extends unknown>(type: ModalTypes, props?: Props) => {
        if (activeRef.current) {
            queueRef.current.push({ type, props: props as Record<string, unknown> })
            return
        }
        activeRef.current = true
        setType(type)
        props && setProps(props as Record<string, unknown>)
    }, [])

    const closeModal = useCallback(() => {
        const next = queueRef.current.shift()
        if (next) {
            setType(next.type)
            setProps(next.props)
        } else {
            activeRef.current = false
            setType(undefined)
            setProps(undefined)
        }
    }, [])

    return (
        <Context.Provider value={{ openModal, closeModal, type, props }}>
            {children}
            <ModalRenderer type={type} />
        </Context.Provider>
    )
}
